/**
 * What may be attached to a deal, and in what format.
 *
 * Both sides need this: the API to police an upload, the console to say what
 * it will accept before the operator picks a file. Two copies would drift,
 * and the way that drift shows up is a file the console offers and the server
 * then refuses — which reads to the user as a bug in the upload, not as a
 * rule they broke.
 */

import { EXPOSED_STATUSES, type DealStatus } from './lifecycle';

/**
 * Whether the paperwork on this deal is now evidence a funder has relied on.
 *
 * Up to funding, a document is working material: a supplier who attached the
 * wrong delivery note should be able to take it back. Once the advance has
 * gone out, somebody made a lending decision on what was attached, so removal
 * would rewrite the basis of that decision. The API refuses it and the console
 * stops offering it — from this one definition, so the button and the rule
 * cannot disagree.
 */
export function documentsAreLocked(status: DealStatus): boolean {
  return EXPOSED_STATUSES.includes(status) || status === 'SETTLED' || status === 'WRITTEN_OFF';
}

/** Document kinds the platform understands; the credit engine counts them. */
export const DOCUMENT_KINDS = [
  'PURCHASE_ORDER',
  'INVOICE',
  'GRN',
  'DELIVERY_NOTE',
  'CONTRACT',
  'CREDIT_NOTE',
  'BANK_STATEMENT',
  'OTHER',
] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

export const DOCUMENT_KIND_LABELS: Record<DocumentKind, string> = {
  PURCHASE_ORDER: 'Purchase order',
  INVOICE: 'Invoice',
  GRN: 'Goods received note',
  DELIVERY_NOTE: 'Delivery note',
  CONTRACT: 'Contract',
  CREDIT_NOTE: 'Credit note',
  BANK_STATEMENT: 'Bank statement',
  OTHER: 'Other',
};

export function documentKindLabel(kind: string): string {
  return DOCUMENT_KIND_LABELS[kind as DocumentKind] ?? kind.replace(/_/g, ' ').toLowerCase();
}

/**
 * Only formats a human reviewer or an OCR pipeline can actually read. An
 * allowlist rather than a blocklist: anything not named here is refused, so a
 * new dangerous type does not become uploadable by default.
 *
 * The extensions are part of the rule, not decoration — a file whose
 * extension contradicts its declared type is refused rather than trusted.
 */
export const ALLOWED_UPLOAD_TYPES: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/tiff': ['.tif', '.tiff'],
  'text/csv': ['.csv'],
  'application/vnd.ms-excel': ['.xls', '.csv'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};

export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

/** Value for a file input's `accept`, derived from the same allowlist. */
export const UPLOAD_ACCEPT = [
  ...Object.keys(ALLOWED_UPLOAD_TYPES),
  ...new Set(Object.values(ALLOWED_UPLOAD_TYPES).flat()),
].join(',');
